import "./TmcOrderComponent.scss";
import template from "./TmcOrderComponent.html";
import {TradefloorApiService} from "../../../../../../../js/services/tradefloorApi.service";
import {FeatureNames} from "../../../../../../../types/UserConfig";
import {PriceStrategyService} from "../../../../../../../components/inputs/price/service/PriceStrategyService";
import {IFormController, IOnDestroy, IOnInit} from "angular";
import {IndexPricingStrategy, PriceStrategy} from "../../../../../../../components/inputs/price/input/PriceStrategy";
import {OptionsWizardOrderLeg, OptionsWizardTmcOrder, TmcTicket} from "../../../../types/OptionsTicket";
import {BaseStrategy} from "../../../../types/WhatIf";
import {OrderTicketUtil} from "../../../../util/OrderTicketUtil";
import {PlaceOrderService} from "../../../../services/PlaceOrderService";
import {PortfolioType} from "../../../../../../models/Portfolio";
import {TmcSpreads} from "../../../models/OrderSpreads";
import {TmcCodeResponse, TmcLeg} from "../../../../services/models/TmcRequests";
import {NumberUtil} from "../../../../../../../js/util/NumberUtil";
import {ChainService, OptionChain} from "../../../../../../../types/ChainService";
import {SecurityUtil} from "../../../../../../../js/util/SecurityUtil";
import {isNotNull} from "../../../../../../../js/util/UtilFunctions";
import {OrderValidation} from "../../../../util/validation/OrderValidation";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {CurrentUserService} from "../../../../../../../services/user/CurrentUserService";
import {Optional} from "../../../../../../../js/util/Optional";
import {ObjectUtil} from "../../../../../../../js/util/ObjectUtil";
import {LegAndPrice, TmcLegModel} from "./models/TmcLegModel";
import {OrderSide} from "../../../../../../../orders/models/OrderRecord";

export const TmcOrderStep = "tmcOrderStep";

export function tmcOrderStep() {
    return {
        template,
        bindings: {
            whatIf: "<",
            targetPrice: "<",
            tmcResponse: "<",
            order: "<",
            portfolioType: "<",
            skipXjoEtrValidation: "<",
            skipEquityEtrValidation: "<",
            onOrderChange: "&"
        },
        controllerAs: "ctrl",
        controller: TmcOrderComponentController
    };
}

const PriceUpdateIntervalSeconds = 5;
const PriceUpdateInterval = PriceUpdateIntervalSeconds * 1000;

export class TmcOrderComponentController implements IOnInit, IOnDestroy {

    /** @input */
    public set whatIf(base: BaseStrategy | null) {
        if (base != null) {
            this.whatIf$.next(base);
        }
    }

    private readonly whatIf$ = new ReplaySubject<BaseStrategy>(1);

    /** @input */
    public set targetPrice(price: number | null) {
        this.targetPrice$.next(price);
    }

    private readonly targetPrice$ = new BehaviorSubject<number | null>(null);

    /** @input */
    public set tmcResponse(response: TmcCodeResponse | null) {
        if (response != null) {
            this.tmcResponse$.next(response);
        }
    }

    private readonly tmcResponse$ = new ReplaySubject<TmcCodeResponse>(1);

    /** @input */
    public set order(order: OptionsWizardTmcOrder | null) {
        this.order$.next(order);
    }

    private readonly order$ = new ReplaySubject<OptionsWizardTmcOrder | null>();

    /** @input */
    public set portfolioType(type: PortfolioType | null) {
        if (type != null) {
            this.portfolioType$.next(type);
        }
    }

    private readonly portfolioType$ = new ReplaySubject<PortfolioType>(1);

    public readonly onOrderChange: Function;
    public readonly skipXjoEtrValidation: boolean | null;
    public readonly skipEquityEtrValidation: boolean | null;
    public readonly isBuy$: Observable<boolean>;
    public readonly isSell$: Observable<boolean>;

    public readonly tmcForm: IFormController;

    public showEtrLimitWarning = false;

    public readonly priceStrategy$ = new ReplaySubject<PriceStrategy>(1);
    public readonly isIndex$ = new BehaviorSubject(false);
    public readonly ticket$ = new ReplaySubject<TmcTicket>(1);
    public readonly legs$ = new BehaviorSubject(TmcLegModel.empty());

    private _chain: Promise<OptionChain> | null;

    public showCalculator = false;

    public quantity: number;

    private readonly spreads$ = new BehaviorSubject<TmcSpreads | null>(null);
    private readonly updateQuantity$ = new BehaviorSubject<number | null>(null);
    private readonly updateLimit$ = new BehaviorSubject<number | null>(null);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly tradefloorApi: TradefloorApiService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly priceStrategyService: PriceStrategyService,
                       private readonly placeOrderService: PlaceOrderService,
                       private readonly chainService: ChainService) {
        this.isBuy$ = this.buildIsBuy();
        this.isSell$ = this.buildIsSell();
    }

    private buildIsBuy(): Observable<boolean> {
        return this.order$
                   .filter(isNotNull)
                   .map((o) => o.ticket)
                   .filter(isNotNull)
                   .map((t) => t.buy)
                   .startWith(true)
                   .publishReplay(1)
                   .refCount();
    }

    private buildIsSell(): Observable<boolean> {
        return this.isBuy$
                   .map((buy) => !buy)
                   .publishReplay(1)
                   .refCount();
    }

    public $onInit() {
        this.updatePrices();
        this.watchTicketSources();
        this.watchPriceStrategySource();
        this.watchOrderSources();
        this.watchOrderChanged();
        this.watchTicketValuesChanged();
        this.watchLegSources();
    }

    private watchTicketSources() {
        const source$ = Observable.combineLatest(this.whatIf$, this.tmcResponse$);

        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
            // WhatIf shouldn't actually change once set, so this is really just making sure we have both and that
            // the TmcResponse hasn't changed
               .distinctUntilChanged(([a1, a2],
                                      [b1, b2]) => (a1 === b1) && ((a2.code === b2.code) && (a2.opposite === b2.opposite)))
               .withLatestFrom(this.order$, this.targetPrice$)
               .map(([[whatIf, tmcResponse], currentOrder, targetPrice]) => this.createTicket(whatIf,
                                                                                              tmcResponse,
                                                                                              currentOrder,
                                                                                              targetPrice))
               .subscribe((ticket) => this.ticket$.next(ticket));
    }

    /*
     * Passing the currentTicket is a bit of a work around for not having a proper chain of methods
     * for building the ticket, and therefore there are a couple of ways that a ticket could be created, this isn't
     * correct, but was needed right now
     */
    private createTicket(whatIf: BaseStrategy,
                         tmcResponse: TmcCodeResponse,
                         currentOrder: OptionsWizardTmcOrder | null,
                         targetPrice: number | null): TmcTicket {
        console.debug("Creating Ticket");
        const ticket = OrderTicketUtil.buildTmc(tmcResponse, whatIf);
        // Keep the old limit if it was set
        ticket.limit = Optional.of(currentOrder)
                               .map((o) => o.ticket)
                               .map((t) => t.limit)
                               .orGet(() => targetPrice)
                               .orNull();
        ticket.quantity = Optional.of(currentOrder)
                                  .map((o) => o.ticket)
                                  .map((t) => t.quantity)
                                  .orElse(ticket.quantity);
        return ticket;
    }

    private watchOrderSources() {
        const source$ = Observable.combineLatest(this.ticket$, this.spreads$, this.priceStrategy$);
        source$.takeUntil(this.destroyed$)
               .observeOn(Scheduler.async)
               .filter(([_, spreads]) => isNotNull(spreads))
               .flatMap(([ticket, spreads, strategy]) => this.buildWizardOrder(ticket, spreads!, strategy))
               .subscribe((order) => this.order$.next(order));
    }

    private async buildWizardOrder(ticket: TmcTicket,
                                   spreads: TmcSpreads,
                                   strategy: PriceStrategy): Promise<OptionsWizardTmcOrder> {
        console.debug("Creating Order");
        const underlying = SecurityUtil.underlying(ticket.code);
        const chain = await this.chainFor(underlying);
        const side = OrderTicketUtil.side(ticket.quantity);
        const legs = ticket.legs.map(leg => this.buildWizardOrderLeg(ticket, leg, spreads, chain));
        const spread = spreads.tmcSpread;

        const mid = Optional.of(spread)
                            .map((s) => s.mid);

        // Set a default limit if there isn't already one set
        ticket.limit = Optional.of(ticket.limit)
                               .or(mid)
                               .map((limit) => strategy.round(limit))
                               .orNull();
        return {
            ticket,
            underlying,
            spread,
            legs,
            side
        };
    }

    private buildWizardOrderLeg(ticket: TmcTicket,
                                leg: TmcLeg,
                                spreads: TmcSpreads,
                                chain: OptionChain): OptionsWizardOrderLeg {
        console.debug("Creating Order leg");
        const spread = spreads.legSpreadFor(leg.code)
                              .orElseThrow(() => Error(`Unable to find spread for leg ${leg.code}`));
        const option = chain.codes[leg.code];
        const underlying = SecurityUtil.underlying(leg.code);
        const quantityMultiplier = SecurityUtil.isEquity(leg.code) ? chain.cvf : 1;
        const strategy = this.priceStrategyService.forCode(underlying);
        const side = leg.ratio > 0 ? OrderSide.BUY : OrderSide.LONG_SELL;
        return {
            code: leg.code,
            underlying,
            strategy,
            ratio: leg.ratio,
            side,
            quantity: leg.ratio * ticket.quantity * quantityMultiplier,
            spread,
            option
        };
    }

    /* Lazy Load the Chain and then keep a reference for when we need to requery it */
    private async chainFor(underlying: string): Promise<OptionChain> {
        if (this._chain == null) {
            this._chain = this.chainService(underlying);
        }
        return this._chain;
    }

    private watchOrderChanged() {
        this.order$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .subscribe((order) => {
                this.onOrderChange({order});
                if (order != null) {
                    this.quantity = order.ticket.quantity;
                    this.validateLimit(order);
                }
            });
    }

    private watchPriceStrategySource() {
        this.ticket$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((ticket) => ticket.code)
            .map((code) => this.priceStrategyService.forCode(code))
            .subscribe((strategy) => {
                this.priceStrategy$.next(strategy);
                const isIndex = strategy instanceof IndexPricingStrategy;
                console.debug("Setting Price Strategy", isIndex);
                this.isIndex$.next(isIndex);
            });
    }

    private updatePrices() {
        let timer$: Observable<number>;
        if (this.currentUserService.hasFeature(FeatureNames.FEATURE_LIVE_PRICES)) {
            timer$ = Observable.timer(0, PriceUpdateInterval);
        } else {
            timer$ = Observable.timer(0).first();
        }
        const source$ = Observable.combineLatest(this.ticket$, this.portfolioType$, timer$);

        source$.takeUntil(this.destroyed$)
               .filter(([_, portfolioType]) => isNotNull(portfolioType))
               .switchMap(([ticket, portfolioType]) => this.retrieveSpreads(ticket, portfolioType!))
               .subscribe((spreads) => this.spreads$.next(spreads));
    }

    private retrieveSpreads(ticket: TmcTicket, portfolioType: PortfolioType): Observable<TmcSpreads> {
        console.debug("Retrieving Spreads");
        return Observable.of(ticket)
                         .flatMap((t) => this.tradefloorApi.retrieveTmcSpread(t.code,
                                                                              t.legs,
                                                                              t.opposite,
                                                                              portfolioType))
                         .map((response) => response.bidAskSummaryList)
                         .map((spreads) => TmcSpreads.from(ticket.code, spreads))
                         .catch(() => Observable.empty<TmcSpreads>());
    }

    private watchTicketValuesChanged() {
        const source$ = Observable.combineLatest(this.updateLimit$, this.updateQuantity$);
        source$.observeOn(Scheduler.async)
               .takeUntil(this.destroyed$)
               .withLatestFrom(this.ticket$)
               .map(([[limit, quantity], ticket]) => this.rebuildTicket(limit, quantity, ticket))
               .subscribe((ticket) => this.ticket$.next(ticket));
    }

    private rebuildTicket(updatedLimit: number | null, updatedQuantity: number | null, ticket: TmcTicket): TmcTicket {
        if ((updatedLimit == null) && (updatedQuantity == null)) {
            return ticket;
        } else {
            console.debug("Rebuilding Ticket");
            const copy = ObjectUtil.copy(ticket);
            const newLimit = Optional.of(updatedLimit)
                                     .or(ticket.limit)
                                     .orNull();
            const newQuantity = Optional.of(updatedQuantity)
                                        .orElse(ticket.quantity);
            return ObjectUtil.merge({}, copy, {limit: newLimit}, {quantity: newQuantity});
        }
    }

    private watchLegSources() {
        this.order$
            .takeUntil(this.destroyed$)
            .observeOn(Scheduler.async)
            .filter(isNotNull)
            .withLatestFrom(this.legs$)
            .map(([order, legs]) => this.rebuildLegs(order, legs))
            .subscribe((legs) => this.legs$.next(legs));
    }

    private rebuildLegs(order: OptionsWizardTmcOrder, legs: TmcLegModel): TmcLegModel {
        const orderLegs = order.legs;
        const legModels = orderLegs.map((l) => this.buildLeg(l, legs));
        return TmcLegModel.of(legModels);
    }

    private buildLeg(leg: OptionsWizardOrderLeg, previousLegs: TmcLegModel): LegAndPrice {
        const price = previousLegs.findLeg(leg.code)
                                  .map((l) => l.price)
                                  .orGet(() => leg.spread.mid)
                                  .orElse(0);
        return {
            leg,
            price
        };
    }

    public setLimit(limit: number) {
        this.updateLimit$.next(limit);
    }

    public setToBid() {
        this.spreads$
            .first()
            .timeout(100)
            .filter(isNotNull)
            .map((spreads) => spreads.tmcSpread)
            .subscribe((spread) => {
                Optional.of(spread.bid)
                        .or(spread.bidLimit)
                        .ifPresent((limit) => this.setLimit(limit));
            });
    }

    public setToAsk() {
        this.spreads$
            .first()
            .timeout(100)
            .filter(isNotNull)
            .map((spreads) => spreads.tmcSpread)
            .subscribe((spread) => {
                Optional.of(spread.ask)
                        .or(spread.askLimit)
                        .ifPresent((limit) => this.setLimit(limit));
            });
    }

    public setLimitToMid() {
        this.spreads$
            .first()
            .timeout(100)
            .filter(isNotNull)
            .map((spreads) => spreads.tmcSpread)
            .subscribe((spread) => {
                Optional.of(spread)
                        .map((s) => s.mid)
                        .ifPresent((limit) => this.setLimit(limit));
            });
    }

    private validateLimit(order: OptionsWizardTmcOrder) {
        if (this.tmcForm != null) {
            this.validateTradingRange(order);
            this.validateTradingState(order);
        }
    }

    private validateTradingRange(order: OptionsWizardTmcOrder) {
        const isXjo = SecurityUtil.underlying(order.ticket.code) === "XJO";

        const orderLimit = order.ticket.limit;

        if (!NumberUtil.isValidNumber(orderLimit)) {
            this.updateTradingRangeLimitValidity(true, false);
        } else if (isXjo && this.skipXjoEtrValidation) {
            this.updateTradingRangeLimitValidity(true, false);
        } else if (!isXjo && this.skipEquityEtrValidation) {
            this.updateTradingRangeLimitValidity(true, false);
        } else {
            const validation = OrderValidation.validateEtr(orderLimit, OrderSide.BUY, order.spread as any, this.currentUserService.isAdviser());
            this.updateTradingRangeLimitValidity(!validation.outsideEtr, validation.etrWarning);
        }
    }

    private updateTradingRangeLimitValidity(valid: boolean, etrWarning: boolean) {
        this.tmcForm.$setValidity("trading-range-limit", valid, this.tmcForm);
        this.showEtrLimitWarning = etrWarning;
    }

    private validateTradingState(order: OptionsWizardTmcOrder) {
        const notSuspended = order.spread.tradingState !== "S";
        this.tmcForm.$setValidity("trading-state", notSuspended, this.tmcForm);
    }

    public quantityChanged(quantity: number) {
        this.updateQuantity$.next(quantity);
    }

    public $onDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
